import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  CheckCircle2,
  ChevronDown,
  Download,
  FileText,
  Loader2,
  Pencil,
  Plus,
  ReceiptText,
  RotateCcw,
  Search,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatError, inr } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  emptyItem,
  emptySupplier,
  formatBillDate,
  inputClass,
  labelClass,
  purchaseTotals,
  today,
} from "./purchase/purchaseUtils";
import { showAdminFieldErrors } from "./adminValidation";

export const PurchaseManagementTab = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [bills, setBills] = useState([]);
  const [supplierForm, setSupplierForm] = useState(emptySupplier);
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [updatingSupplierId, setUpdatingSupplierId] = useState(null);
  const [archiveCandidate, setArchiveCandidate] = useState(null);
  const [billForm, setBillForm] = useState({
    supplier_id: "",
    bill_number: "",
    bill_date: today(),
    items: [emptyItem()],
    attachment: null,
  });
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    supplier_id: "",
    status: "",
  });
  const [loading, setLoading] = useState(true);
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [savingBill, setSavingBill] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [updatingBillId, setUpdatingBillId] = useState(null);

  const load = useCallback(async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(nextFilters).filter(([, value]) => value),
      );
      const [supplierResponse, billResponse] = await Promise.all([
        api.get("/admin/purchase-suppliers", { params: { include_archived: true } }),
        api.get("/admin/purchase-bills", { params }),
      ]);
      setSuppliers(supplierResponse.data);
      setBills(billResponse.data);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => purchaseTotals(billForm.items), [billForm.items]);
  const activeSuppliers = useMemo(
    () => suppliers.filter((supplier) => !supplier.archived),
    [suppliers],
  );
  const matchingSuppliers = useMemo(() => {
    const query = supplierSearch.trim().toLowerCase();
    if (!query) {
      return [];
    }
    return activeSuppliers.filter((supplier) => (
      supplier.name.toLowerCase().includes(query)
      || (supplier.gst_number || "").toLowerCase().includes(query)
    )).slice(0, 8);
  }, [activeSuppliers, supplierSearch]);

  const resetSupplier = () => {
    setSupplierForm(emptySupplier);
    setEditingSupplierId(null);
  };

  const saveSupplier = async (event) => {
    event.preventDefault();
    setSavingSupplier(true);
    try {
      if (editingSupplierId) {
        await api.put(`/admin/purchase-suppliers/${editingSupplierId}`, supplierForm);
        toast.success("Supplier updated");
      } else {
        await api.post("/admin/purchase-suppliers", supplierForm);
        toast.success("Supplier added");
      }
      resetSupplier();
      await load();
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingSupplier(false);
    }
  };

  const editSupplier = (supplier) => {
    setEditingSupplierId(supplier.id);
    setSupplierForm({
      name: supplier.name || "",
      gst_number: supplier.gst_number || "",
      address: supplier.address || "",
      contact_person: supplier.contact_person || "",
      phone: supplier.phone || "",
      email: supplier.email || "",
    });
  };

  const chooseSupplier = (supplier) => {
    setBillForm((current) => ({ ...current, supplier_id: supplier.id }));
    setSupplierSearch(supplier.name);
    setSupplierPickerOpen(false);
  };

  const clearSupplierSelection = () => {
    setBillForm((current) => ({ ...current, supplier_id: "" }));
    setSupplierSearch("");
    setSupplierPickerOpen(false);
  };

  const updateSupplierArchive = async (supplier, archived) => {
    setUpdatingSupplierId(supplier.id);
    try {
      const action = archived ? "archive" : "restore";
      await api.post(`/admin/purchase-suppliers/${supplier.id}/${action}`);
      toast.success(archived ? "Supplier archived" : "Supplier restored");
      if (billForm.supplier_id === supplier.id && archived) {
        clearSupplierSelection();
      }
      await load();
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setUpdatingSupplierId(null);
    }
  };

  const updateBillItem = (index, key, value) => {
    setBillForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      )),
    }));
  };

  const uploadAttachment = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/admin/purchase-bills/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBillForm((current) => ({ ...current, attachment: response.data }));
      toast.success("Bill attachment uploaded");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setUploading(false);
    }
  };

  const saveBill = async (event) => {
    event.preventDefault();
    if (!billForm.supplier_id) {
      showAdminFieldErrors("Search and select a supplier before saving", [
        "purchase-bill-supplier-search",
      ]);
      return;
    }
    if (!billForm.attachment) {
      showAdminFieldErrors("Upload the purchase bill before saving", [
        "purchase-bill-upload-label",
      ]);
      return;
    }
    setSavingBill(true);
    try {
      await api.post("/admin/purchase-bills", {
        ...billForm,
        amount: Number(totals.amount.toFixed(2)),
        gst_amount: Number(totals.gstAmount.toFixed(2)),
        total_amount: Number(totals.totalAmount.toFixed(2)),
        items: billForm.items.map((item) => ({
          ...item,
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price),
          gst_rate: Number(item.gst_rate),
        })),
      });
      toast.success("Purchase bill recorded");
      setBillForm({
        supplier_id: "",
        bill_number: "",
        bill_date: today(),
        items: [emptyItem()],
        attachment: null,
      });
      await load();
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingBill(false);
    }
  };

  const setPaymentStatus = async (bill, status) => {
    setUpdatingBillId(bill.id);
    try {
      await api.patch(`/admin/purchase-bills/${bill.id}/payment-status`, { status });
      toast.success(status === "paid" ? "Bill marked paid" : "Bill marked unpaid");
      await load();
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setUpdatingBillId(null);
    }
  };

  const applyFilters = async () => {
    await load(filters);
  };

  const clearFilters = async () => {
    const cleared = { start_date: "", end_date: "", supplier_id: "", status: "" };
    setFilters(cleared);
    await load(cleared);
  };

  const exportBills = async () => {
    setExporting(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value),
      );
      const response = await api.get("/admin/purchase-bills/export.xlsx", {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "eira-purchase-bills-export.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Purchase bill report downloaded");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-7xl" data-testid="purchase-management-tab">
      <header className="border-b border-[#E2DCD2] pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
          Accounts payable
        </p>
        <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1E2A24]">
          Purchase Management
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-[#64748B]">
          Maintain supplier records, retain original bills, and prepare purchase data for
          accounting.
        </p>
      </header>

      <div className="mt-8 grid gap-8">
        <details
          className="group order-2 border border-[#E2DCD2] bg-white"
          data-testid="purchase-supplier-section"
        >
          <summary
            className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 sm:p-6"
            data-testid="supplier-management-toggle"
          >
            <span>
              <span
                className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#8C6D46]"
              >
                Supplier directory
              </span>
              <span className="mt-1 block font-serif text-2xl font-semibold text-[#1E2A24]">
                Manage suppliers
              </span>
            </span>
            <span className="flex items-center gap-3 text-sm font-semibold text-[#64748B]">
              <span data-testid="supplier-count">{activeSuppliers.length} active</span>
              <ChevronDown
                size={18}
                className="transition-transform duration-200 group-open:rotate-180"
                data-testid="supplier-management-disclosure-icon"
              />
            </span>
          </summary>
          <div className="border-t border-[#E2DCD2] p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
            <div>
              <p className={labelClass}>Supplier directory</p>
              <h3 className="mt-2 font-serif text-2xl font-semibold text-[#1E2A24]">
                {editingSupplierId ? "Edit supplier" : "Add supplier"}
              </h3>
            </div>
            {editingSupplierId && (
              <button
                type="button"
                onClick={resetSupplier}
                className="text-xs font-semibold text-[#64748B] hover:text-[#1E2A24]"
                data-testid="cancel-supplier-edit"
              >
                Cancel
              </button>
            )}
          </div>
            <form
            className="mt-5 space-y-4"
            onSubmit={saveSupplier}
            data-testid="purchase-supplier-form"
          >
            <label className={labelClass}>
              Supplier name
              <input
                required
                value={supplierForm.name}
                onChange={(event) => setSupplierForm({
                  ...supplierForm,
                  name: event.target.value,
                })}
                className={inputClass}
                data-testid="supplier-name-input"
              />
            </label>
            <label className={labelClass}>
              GST number
              <input
                value={supplierForm.gst_number}
                onChange={(event) => setSupplierForm({
                  ...supplierForm,
                  gst_number: event.target.value,
                })}
                className={inputClass}
                data-testid="supplier-gst-input"
              />
            </label>
            <label className={labelClass}>
              Full address
              <textarea
                value={supplierForm.address}
                onChange={(event) => setSupplierForm({
                  ...supplierForm,
                  address: event.target.value,
                })}
                rows={3}
                className={inputClass}
                data-testid="supplier-address-input"
              />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className={labelClass}>
                Contact person
                <input
                  value={supplierForm.contact_person}
                  onChange={(event) => setSupplierForm({
                    ...supplierForm,
                    contact_person: event.target.value,
                  })}
                  className={inputClass}
                  data-testid="supplier-contact-person-input"
                />
              </label>
              <label className={labelClass}>
                Phone
                <input
                  value={supplierForm.phone}
                  onChange={(event) => setSupplierForm({
                    ...supplierForm,
                    phone: event.target.value,
                  })}
                  className={inputClass}
                  data-testid="supplier-phone-input"
                />
              </label>
            </div>
            <label className={labelClass}>
              Email
              <input
                type="email"
                value={supplierForm.email}
                onChange={(event) => setSupplierForm({
                  ...supplierForm,
                  email: event.target.value,
                })}
                className={inputClass}
                data-testid="supplier-email-input"
              />
            </label>
            <button
              type="submit"
              disabled={savingSupplier}
              className={[
                "inline-flex items-center gap-2 bg-[#2D5A47] px-4 py-2.5 text-sm",
                "font-semibold text-white hover:bg-[#214435] disabled:opacity-50",
              ].join(" ")}
              data-testid="save-supplier-button"
            >
              {savingSupplier ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Plus size={15} />
              )}
              {savingSupplier ? "Saving…" : editingSupplierId ? "Save supplier" : "Add supplier"}
            </button>
            </form>
            <div className="mt-7 border-t border-[#E2DCD2] pt-5" data-testid="supplier-list">
            <div className="flex items-center justify-between gap-3">
              <p className={labelClass}>Saved suppliers</p>
              <span
                className="text-xs font-semibold text-[#64748B]"
                data-testid="supplier-management-count"
              >
                {suppliers.length}
              </span>
            </div>
            {loading ? (
              <p className="mt-3 text-sm text-[#64748B]">Loading suppliers…</p>
            ) : suppliers.length === 0 ? (
              <p className="mt-3 text-sm text-[#64748B]" data-testid="supplier-empty-state">
                No suppliers yet.
              </p>
            ) : (
              <div
                className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1"
                data-testid="supplier-list-scroll"
              >
                {suppliers.map((supplier) => (
                  <div
                    key={supplier.id}
                    className={[
                      "flex items-start justify-between gap-3 border border-[#E2DCD2]",
                      "bg-[#FDFBF7] p-3",
                    ].join(" ")}
                    data-testid={`supplier-row-${supplier.id}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[#1E2A24]">
                        {supplier.name}
                      </p>
                      <p className="mt-1 text-xs text-[#64748B]">
                        {supplier.gst_number || "GSTIN not added"}
                      </p>
                      {supplier.archived && (
                        <p className="mt-1 text-xs font-semibold text-amber-800">Archived</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => editSupplier(supplier)}
                      className="shrink-0 p-2 text-[#2D5A47] hover:bg-[#EBF2EE]"
                      title="Edit supplier"
                      data-testid={`edit-supplier-${supplier.id}`}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (supplier.archived) {
                          updateSupplierArchive(supplier, false);
                        } else {
                          setArchiveCandidate(supplier);
                        }
                      }}
                      disabled={updatingSupplierId === supplier.id}
                      className={[
                        "shrink-0 p-2 text-[#2D5A47] hover:bg-[#EBF2EE]",
                        "disabled:opacity-50",
                      ].join(" ")}
                      title={supplier.archived ? "Restore supplier" : "Archive supplier"}
                      data-testid={[
                        supplier.archived ? "restore" : "archive",
                        "supplier",
                        supplier.id,
                      ].join("-")}
                    >
                      {updatingSupplierId === supplier.id ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : supplier.archived ? (
                        <RotateCcw size={15} />
                      ) : (
                        <Archive size={15} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
              )}
            </div>
          </div>
        </details>

        <section
          className="order-1 border border-[#D9C3A5] bg-[#FDFBF7] p-5 sm:p-6"
          data-testid="purchase-bill-entry-section"
        >
          <p className={labelClass}>Bill register</p>
          <h3 className="mt-2 font-serif text-2xl font-semibold text-[#1E2A24]">
            Record purchase bill
          </h3>
          <form
            className="mt-5 space-y-5"
            onSubmit={saveBill}
            data-testid="purchase-bill-form"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className={labelClass}>Supplier</p>
                <div className="relative mt-1">
                  <Search
                    size={16}
                    className={[
                      "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2",
                      "text-[#64748B]",
                    ].join(" ")}
                  />
                  <input
                    value={supplierSearch}
                    onFocus={() => setSupplierPickerOpen(true)}
                    onChange={(event) => {
                      setSupplierSearch(event.target.value);
                      setBillForm((current) => ({ ...current, supplier_id: "" }));
                      setSupplierPickerOpen(true);
                    }}
                    placeholder="Search name or GSTIN"
                    className={[
                      "w-full border border-[#E2DCD2] bg-white py-2 pl-10 pr-10 text-sm",
                      "text-[#1E2A24] outline-none focus:border-[#2D5A47]",
                    ].join(" ")}
                    aria-expanded={supplierPickerOpen}
                    aria-controls="purchase-supplier-search-results"
                    data-testid="purchase-bill-supplier-search"
                  />
                  {billForm.supplier_id && (
                    <button
                      type="button"
                      onClick={clearSupplierSelection}
                      className={[
                        "absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#64748B]",
                        "hover:text-[#1E2A24]",
                      ].join(" ")}
                      title="Clear supplier selection"
                      data-testid="clear-purchase-bill-supplier"
                    >
                      <X size={15} />
                    </button>
                  )}
                  {supplierPickerOpen && supplierSearch.trim() && (
                    <div
                      id="purchase-supplier-search-results"
                      className={[
                        "absolute z-20 mt-1 max-h-56 w-full overflow-y-auto border",
                        "border-[#D9C3A5] bg-white shadow-lg",
                      ].join(" ")}
                      data-testid="purchase-bill-supplier-results"
                    >
                      {matchingSuppliers.length === 0 ? (
                        <p
                          className="px-3 py-3 text-sm text-[#64748B]"
                          data-testid="supplier-search-empty"
                        >
                          No active supplier matches this search.
                        </p>
                      ) : (
                        matchingSuppliers.map((supplier) => (
                          <button
                            key={supplier.id}
                            type="button"
                            onClick={() => chooseSupplier(supplier)}
                            className={[
                              "block w-full border-b border-[#EDE8DF] px-3 py-2.5 text-left",
                              "hover:bg-[#EBF2EE]",
                            ].join(" ")}
                            data-testid={`select-purchase-supplier-${supplier.id}`}
                          >
                            <span className="block text-sm font-semibold text-[#1E2A24]">
                              {supplier.name}
                            </span>
                            <span className="mt-0.5 block text-xs text-[#64748B]">
                              {supplier.gst_number || "GSTIN not added"}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {billForm.supplier_id && (
                  <p
                    className="mt-1 text-xs text-[#2D5A47]"
                    data-testid="selected-purchase-supplier"
                  >
                    Supplier selected
                  </p>
                )}
              </div>
              <label className={labelClass}>
                Bill number
                <input
                  required
                  value={billForm.bill_number}
                  onChange={(event) => setBillForm({
                    ...billForm,
                    bill_number: event.target.value,
                  })}
                  className={inputClass}
                  data-testid="purchase-bill-number-input"
                />
              </label>
              <label className={labelClass}>
                Bill date
                <input
                  required
                  type="date"
                  value={billForm.bill_date}
                  onChange={(event) => setBillForm({
                    ...billForm,
                    bill_date: event.target.value,
                  })}
                  className={inputClass}
                  data-testid="purchase-bill-date-input"
                />
              </label>
              <div>
                <p className={labelClass}>Bill attachment</p>
                <label
                  className={[
                    "mt-1 flex cursor-pointer items-center justify-center gap-2 border",
                    "border-dashed border-[#8C6D46] bg-white px-3 py-2.5 text-sm",
                    "font-semibold text-[#2D5A47] hover:bg-[#EBF2EE]",
                  ].join(" ")}
                  data-testid="purchase-bill-upload-label"
                >
                  {uploading ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Upload size={15} />
                  )}
                  {uploading ? "Uploading…" : "Upload PDF or image"}
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={uploadAttachment}
                    className="hidden"
                    disabled={uploading}
                    data-testid="purchase-bill-upload-input"
                  />
                </label>
                {billForm.attachment && (
                  <div
                    className="mt-2 flex items-center justify-between gap-2 text-xs text-[#1E2A24]"
                    data-testid="purchase-bill-uploaded-file"
                  >
                    <span className="truncate">{billForm.attachment.name}</span>
                    <button
                      type="button"
                      onClick={() => setBillForm({ ...billForm, attachment: null })}
                      className="p-1 text-red-700 hover:bg-red-50"
                      title="Remove attachment"
                      data-testid="remove-purchase-bill-attachment"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div data-testid="purchase-bill-items">
              <div className="flex items-center justify-between gap-3">
                <p className={labelClass}>Purchased items</p>
                <button
                  type="button"
                  onClick={() => setBillForm({
                    ...billForm,
                    items: [...billForm.items, emptyItem()],
                  })}
                  className={[
                    "inline-flex items-center gap-1 text-xs font-semibold text-[#2D5A47]",
                    "hover:text-[#214435]",
                  ].join(" ")}
                  data-testid="add-purchase-bill-item"
                >
                  <Plus size={14} /> Add item
                </button>
              </div>
              <div className="mt-2 space-y-3">
                {billForm.items.map((item, index) => (
                  <div
                    key={index}
                    className="grid gap-3 border border-[#E2DCD2] bg-white p-3 sm:grid-cols-12"
                    data-testid={`purchase-bill-item-${index}`}
                  >
                    <label className={`sm:col-span-4 ${labelClass}`}>
                      Item
                      <input
                        required
                        value={item.name}
                        onChange={(event) => updateBillItem(index, "name", event.target.value)}
                        className={inputClass}
                        data-testid={`purchase-item-name-${index}`}
                      />
                    </label>
                    <label className={`sm:col-span-2 ${labelClass}`}>
                      Qty
                      <input
                        required
                        min="0.01"
                        step="0.01"
                        type="number"
                        value={item.quantity}
                        onChange={(event) => updateBillItem(index, "quantity", event.target.value)}
                        className={inputClass}
                        data-testid={`purchase-item-quantity-${index}`}
                      />
                    </label>
                    <label className={`sm:col-span-2 ${labelClass}`}>
                      Unit price
                      <input
                        required
                        min="0"
                        step="0.01"
                        type="number"
                        value={item.unit_price}
                        onChange={(event) => (
                          updateBillItem(index, "unit_price", event.target.value)
                        )}
                        className={inputClass}
                        data-testid={`purchase-item-unit-price-${index}`}
                      />
                    </label>
                    <label className={`sm:col-span-2 ${labelClass}`}>
                      GST %
                      <input
                        required
                        min="0"
                        max="100"
                        step="0.01"
                        type="number"
                        value={item.gst_rate}
                        onChange={(event) => (
                          updateBillItem(index, "gst_rate", event.target.value)
                        )}
                        className={inputClass}
                        data-testid={`purchase-item-gst-rate-${index}`}
                      />
                    </label>
                    <div className="flex items-end justify-end sm:col-span-2">
                      <button
                        type="button"
                        onClick={() => setBillForm({
                          ...billForm,
                          items: billForm.items.filter((_, itemIndex) => itemIndex !== index),
                        })}
                        disabled={billForm.items.length === 1}
                        className="p-2 text-red-700 hover:bg-red-50 disabled:opacity-40"
                        title="Remove item"
                        data-testid={`remove-purchase-item-${index}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="grid gap-px border border-[#1E2A24] bg-[#1E2A24] sm:grid-cols-3"
              data-testid="purchase-bill-totals"
            >
              {[
                ["Amount", totals.amount],
                ["GST amount", totals.gstAmount],
                ["Total amount", totals.totalAmount],
              ].map(([label, value]) => (
                <div key={label} className="bg-white p-3">
                  <p className="text-xs text-[#64748B]">{label}</p>
                  <p className="mt-1 font-mono text-lg font-semibold text-[#1E2A24]">
                    {inr(value)}
                  </p>
                </div>
              ))}
            </div>
            <button
              type="submit"
              disabled={savingBill || uploading || activeSuppliers.length === 0}
              className={[
                "inline-flex items-center gap-2 bg-[#C86D42] px-5 py-3 text-sm",
                "font-semibold text-white hover:bg-[#AE5931] disabled:opacity-50",
              ].join(" ")}
              data-testid="save-purchase-bill-button"
            >
              {savingBill ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ReceiptText size={16} />
              )}
              {savingBill ? "Saving bill…" : "Record purchase bill"}
            </button>
          </form>
        </section>
      </div>

      <section
        className="mt-8 min-w-0 border border-[#E2DCD2] bg-white p-5 sm:p-6"
        data-testid="purchase-bill-register"
      >
        <div
          className={[
            "flex flex-col justify-between gap-4 border-b border-[#E2DCD2] pb-5",
            "lg:flex-row lg:items-end",
          ].join(" ")}
        >
          <div>
            <p className={labelClass}>Purchase register</p>
            <h3 className="mt-2 font-serif text-2xl font-semibold text-[#1E2A24]">
              All purchase bills
            </h3>
          </div>
          <button
            type="button"
            onClick={exportBills}
            disabled={exporting}
            className={[
              "inline-flex w-fit items-center gap-2 border border-[#2D5A47] px-4 py-2",
              "text-sm font-semibold text-[#2D5A47] hover:bg-[#EBF2EE] disabled:opacity-50",
            ].join(" ")}
            data-testid="export-purchase-bills-button"
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {exporting ? "Preparing export…" : "Export Excel"}
          </button>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-5" data-testid="purchase-bill-filters">
          <label className={labelClass}>
            From
            <input
              type="date"
              value={filters.start_date}
              onChange={(event) => setFilters({
                ...filters,
                start_date: event.target.value,
              })}
              className={inputClass}
              data-testid="purchase-filter-start-date"
            />
          </label>
          <label className={labelClass}>
            To
            <input
              type="date"
              value={filters.end_date}
              onChange={(event) => setFilters({
                ...filters,
                end_date: event.target.value,
              })}
              className={inputClass}
              data-testid="purchase-filter-end-date"
            />
          </label>
          <label className={labelClass}>
            Supplier
            <select
              value={filters.supplier_id}
              onChange={(event) => setFilters({
                ...filters,
                supplier_id: event.target.value,
              })}
              className={inputClass}
              data-testid="purchase-filter-supplier"
            >
              <option value="">All suppliers</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            Status
            <select
              value={filters.status}
              onChange={(event) => setFilters({
                ...filters,
                status: event.target.value,
              })}
              className={inputClass}
              data-testid="purchase-filter-status"
            >
              <option value="">All statuses</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </label>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={applyFilters}
              className="bg-[#2D5A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#214435]"
              data-testid="apply-purchase-filters"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="px-3 py-2 text-sm font-semibold text-[#64748B] hover:text-[#1E2A24]"
              data-testid="clear-purchase-filters"
            >
              Clear
            </button>
          </div>
        </div>
        {loading ? (
          <div
            className="flex min-h-48 items-center justify-center text-sm text-[#64748B]"
            data-testid="purchase-bills-loading"
          >
            <Loader2 size={17} className="mr-2 animate-spin" /> Loading purchase bills…
          </div>
        ) : bills.length === 0 ? (
          <p
            className="py-14 text-center text-sm text-[#64748B]"
            data-testid="purchase-bills-empty-state"
          >
            No purchase bills match this view.
          </p>
        ) : (
          <div
            className="admin-table-scroll mt-5 w-full min-w-0"
            data-testid="purchase-bills-table-scroll"
          >
            <table className="w-full min-w-[1120px] text-sm">
              <thead className="bg-[#F4F1EA] text-left text-xs text-[#64748B]">
                <tr>
                  {[
                    "Bill", "Supplier", "Date", "Amount", "GST", "Total", "Status",
                    "Attachment", "Action",
                  ].map((heading) => <th key={heading} className="p-3">{heading}</th>)}
                </tr>
              </thead>
              <tbody>
                {bills.map((bill) => (
                  <tr
                    key={bill.id}
                    className="border-t border-[#E2DCD2]"
                    data-testid={`purchase-bill-row-${bill.id}`}
                  >
                    <td className="p-3 font-mono text-xs text-[#1E2A24]">
                      {bill.bill_number}
                    </td>
                    <td className="p-3">
                      <p className="font-semibold text-[#1E2A24]">
                        {bill.supplier_name}
                      </p>
                      <p className="mt-1 text-xs text-[#64748B]">
                        {bill.supplier_gst_number || "GSTIN not added"}
                      </p>
                    </td>
                    <td className="p-3 text-[#4B5563]">
                      {formatBillDate(bill.bill_date)}
                    </td>
                    <td className="p-3 font-mono">{inr(bill.amount)}</td>
                    <td className="p-3 font-mono">{inr(bill.gst_amount)}</td>
                    <td className="p-3 font-mono font-semibold">
                      {inr(bill.total_amount)}
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold ${
                          bill.payment_status === "paid"
                            ? "bg-emerald-50 text-emerald-800"
                            : "bg-amber-50 text-amber-800"
                        }`}
                        data-testid={`purchase-bill-status-${bill.id}`}
                      >
                        {bill.payment_status === "paid" ? "Paid" : "Unpaid"}
                      </span>
                    </td>
                    <td className="p-3">
                      <a
                        href={bill.attachment?.url}
                        target="_blank"
                        rel="noreferrer"
                        className={[
                          "inline-flex items-center gap-1 text-xs font-semibold text-[#2D5A47]",
                          "hover:text-[#214435]",
                        ].join(" ")}
                        data-testid={`view-purchase-bill-attachment-${bill.id}`}
                      >
                        <FileText size={14} /> View bill
                      </a>
                    </td>
                    <td className="p-3">
                      <button
                        type="button"
                        onClick={() => setPaymentStatus(
                          bill,
                          bill.payment_status === "paid" ? "unpaid" : "paid",
                        )}
                        disabled={updatingBillId === bill.id}
                        className={[
                          "inline-flex items-center gap-1 border border-[#2D5A47] px-3 py-1.5",
                          "text-xs font-semibold text-[#2D5A47] hover:bg-[#EBF2EE]",
                          "disabled:opacity-50",
                        ].join(" ")}
                        data-testid={`toggle-purchase-bill-payment-${bill.id}`}
                      >
                        {updatingBillId === bill.id ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <CheckCircle2 size={13} />
                        )}
                        {bill.payment_status === "paid" ? "Mark unpaid" : "Mark paid"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AlertDialog
        open={Boolean(archiveCandidate)}
        onOpenChange={(open) => {
          if (!open) {
            setArchiveCandidate(null);
          }
        }}
      >
        <AlertDialogContent data-testid="archive-supplier-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this supplier?</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveCandidate?.name} will be hidden from new purchase bills. Existing purchase
              bills stay unchanged, and you can restore this supplier later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-archive-supplier-button">
              Keep supplier
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (archiveCandidate) {
                  updateSupplierArchive(archiveCandidate, true);
                }
                setArchiveCandidate(null);
              }}
              className="bg-[#B6473F] text-white hover:bg-[#923833]"
              data-testid="confirm-archive-supplier-button"
            >
              Archive supplier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};